import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Permission } from '../types/user.types';
import { useDarkMode } from '../ui/darkMode';
import { UI_THEME } from '../ui/theme';
import NotificationBell from './NotificationBell';
import Button from './ui/Button';

const ROLE_LABEL: Record<string, string> = {
  ADMIN: 'Administrador',
  ORCAMENTO: 'Orçamento',
  SECRETARIA: 'Secretaria',
  CONVENIOS: 'Convênios',
  OPERADOR: 'Operador',
  ANALISTA: 'Analista',
  JURIDICO: 'Procuradoria Geral Municipal',
  JURIDICO_ANALISTA: 'Procuradoria Geral Municipal',
  JURIDICO_AUXILIAR: 'Auxiliar PGM',
  GESTOR_JURIDICO: 'Gestor Jurídico',
  CAMARA: 'Câmara Municipal',
  VEREADOR: 'Parlamentar',
};

function initials(name?: string | null): string {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');
}

const HelpMenu: React.FC = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const basePath = window.location.pathname.startsWith('/emendas') ? '/emendas' : '';

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const itemClass = 'block w-full px-3 py-2 text-left text-xs font-semibold text-fg hover:bg-surface-muted';

  return (
    <div ref={ref} className="relative hidden sm:block">
      <Button variant="secondary" size="sm" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        Ajuda
      </Button>
      {open && (
        <div role="menu" className="absolute right-0 top-full z-50 mt-2 w-48 overflow-hidden rounded-lg border border-line bg-surface shadow-lg">
          <a role="menuitem" href={`${basePath}/guia/guia-entidade.html`} target="_blank" rel="noopener noreferrer" className={itemClass} onClick={() => setOpen(false)}>
            Guia da entidade
          </a>
          <Link role="menuitem" to="/contato" className={itemClass} onClick={() => setOpen(false)}>
            Fale conosco
          </Link>
        </div>
      )}
    </div>
  );
};

function currentBreadcrumb(pathname: string): { section: string; page: string } {
  if (pathname.includes('/relatorios/pendencias')) return { section: 'Relatórios', page: 'Pendências' };
  if (pathname.includes('/execucao-financeira')) return { section: 'Gestão', page: 'Execução Financeira' };
  if (pathname.includes('/relatorios/emendas')) return { section: 'Relatórios', page: 'Emendas' };
  if (pathname.includes('/relatorios/instituicoes')) return { section: 'Relatórios', page: 'Instituições' };
  if (pathname.includes('/relatorios/pre-projetos')) return { section: 'Relatórios', page: 'Pré-projetos' };
  if (pathname.includes('/cadastro-emenda')) return { section: 'Gestão', page: 'Cadastro de Emenda' };
  if (pathname.includes('/pre-projetos/novo')) return { section: 'Gestão', page: 'Novo Pré-projeto' };
  if (pathname.includes('/pre-projetos/')) return { section: 'Gestão', page: 'Detalhes do Pré-projeto' };
  if (pathname.includes('/pre-projetos')) return { section: 'Gestão', page: 'Pré-projetos' };
  if (pathname.includes('/cadastro-dados-institucionais')) return { section: 'Gestão', page: 'Cadastro Institucional' };
  if (pathname.includes('/cadastro-dirigente')) return { section: 'Gestão', page: 'Cadastro de Dirigente' };
  if (pathname.includes('/emendas/visao-geral')) return { section: 'Gestão', page: 'Visão Geral' };
  if (pathname.includes('/emendas/')) return { section: 'Gestão', page: 'Detalhes da Emenda' };
  if (pathname.includes('/emendas')) return { section: 'Gestão', page: 'Emendas' };
  if (pathname.includes('/plano/full') || pathname.includes('/novo-plano') || pathname.includes('/editar-plano')) return { section: 'Gestão', page: 'Plano de Trabalho' };
  if (pathname.includes('/plano/prestacao-contas')) return { section: 'Gestão', page: 'Prestação de Contas' };
  if (pathname.includes('/plano/') && pathname.includes('/analise-juridica')) return { section: 'Gestão', page: 'Análise Jurídica' };
  if (pathname.includes('/instituicoes')) return { section: 'Gestão', page: 'Instituições' };
  if (pathname.includes('/diretoria')) return { section: 'Gestão', page: 'Diretoria' };
  if (pathname.includes('/parlamentares')) return { section: 'Gestão', page: 'Parlamentares' };
  if (pathname.includes('/gestao/planos-trabalho')) return { section: 'Gestão', page: 'Planos de Trabalho' };
  if (pathname.includes('/gestao/prestacao-contas-planos')) return { section: 'Gestão', page: 'Prestação de Contas' };
  if (pathname.includes('/usuarios')) return { section: 'Administrador', page: 'Usuários' };
  if (pathname.includes('/tipos-documento-config')) return { section: 'Administrador', page: 'Tipos de Documentos' };
  if (pathname.includes('/admin/permissoes')) return { section: 'Administrador', page: 'Permissões' };
  if (pathname.includes('/admin/emails')) return { section: 'Administrador', page: 'Gerenciamento de E-mails' };
  if (pathname.includes('/admin/parametros')) return { section: 'Administrador', page: 'Parâmetros do Sistema' };
  if (pathname.includes('/admin/funcionalidades')) return { section: 'Administrador', page: 'Funcionalidades' };
  if (pathname.includes('/admin/parlamentar-limites')) return { section: 'Administrador', page: 'Limites Parlamentares' };
  if (pathname.includes('/admin/comissoes')) return { section: 'Administrador', page: 'Comissões' };
  if (pathname.includes('/leis-orcamentarias')) return { section: 'Gestão', page: 'Leis Orçamentárias' };
  if (pathname.includes('/solicitacoes')) return { section: 'Gestão', page: 'Suporte e Solicitações' };
  if (pathname.includes('/profile')) return { section: 'Conta', page: 'Meu Perfil' };
  if (pathname.includes('/gestor-juridico')) return { section: 'Gestão', page: 'Fila Jurídica' };
  if (pathname.includes('/workflow')) return { section: 'Dashboard', page: 'Fluxo de Tramitação' };
  return { section: 'Dashboard', page: 'Área de Trabalho' };
}

const ThemeButton: React.FC<{ isDark: boolean; onClick: () => void; saas?: boolean }> = ({ isDark, onClick, saas = false }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={isDark ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
    title={isDark ? 'Tema claro' : 'Tema escuro'}
    className={`flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border transition ${
      saas
        ? 'border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800'
    }`}
  >
    {isDark ? (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
      </svg>
    ) : (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
      </svg>
    )}
  </button>
);

const UserMenu: React.FC<{
  name: string;
  identityLabel: string;
  saas?: boolean;
}> = ({ name, identityLabel, saas = false }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleMeuPerfil = () => {
    setOpen(false);
    navigate('/dashboard/profile');
  };

  const handleSair = () => {
    setOpen(false);
    logout();
    navigate('/');
  };

  const nameClass = saas
    ? 'text-sm font-semibold text-gray-900 dark:text-white'
    : 'text-sm font-semibold text-slate-900 dark:text-slate-100';
  const identityClass = saas
    ? 'text-xs font-semibold text-teal-700 dark:text-teal-300'
    : 'text-xs font-medium text-slate-500 dark:text-slate-400';
  const avatarClass = saas
    ? 'flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-100 text-[12px] font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200'
    : 'flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-100 text-[12px] font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2.5 rounded-full transition hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <div className="hidden text-right leading-tight sm:block">
          <div className={nameClass}>{name}</div>
          <div className={identityClass}>{identityLabel}</div>
        </div>
        <span className={avatarClass}>{initials(name)}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+8px)] z-50 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white py-1.5 shadow-lg dark:border-slate-700 dark:bg-slate-900"
        >
          <button
            type="button"
            role="menuitem"
            onClick={handleMeuPerfil}
            className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Meu perfil
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleSair}
            className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
          >
            Sair
          </button>
        </div>
      )}
    </div>
  );
};

const DashboardTopBar: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const location = useLocation();
  const [isDark, toggleDark] = useDarkMode();
  const roleKey = String(user?.role ?? '').toUpperCase();
  const isOperadorSecretaria = roleKey === 'OPERADOR' && Boolean(user?.operadorSecretaria);
  const roleLabel = user?.gestorParceria
    ? 'Gestor da Parceria'
    : isOperadorSecretaria ? ROLE_LABEL.SECRETARIA : (ROLE_LABEL[roleKey] ?? roleKey);
  const secretariaLabel = roleKey === 'SECRETARIA' || isOperadorSecretaria ? user?.secretaria?.trim() : undefined;
  const identityLabel = secretariaLabel ? `${roleLabel} · ${secretariaLabel}` : roleLabel;
  const breadcrumbBase = currentBreadcrumb(location.pathname);
  // Quem só abre pedidos (vereador) vê o nome simples; a equipe vê o de gestão.
  const breadcrumb = location.pathname.includes('/solicitacoes') && !hasPermission(Permission.TRATAR_SOLICITACAO_ALTERACAO)
    ? { section: 'Inicio', page: 'Fale com a equipe' }
    : breadcrumbBase;
  const isSaas = UI_THEME === 'saas';

  if (isSaas) {
    return (
      <header className="dashboard-topbar-height sticky top-0 z-40 flex items-center justify-between border-b border-gray-200 bg-white px-6 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center gap-3">
          <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg bg-teal-600 text-[11px] font-bold tracking-[0.04em] text-white">
            EC
          </span>
          <div>
            <div className="text-sm font-bold text-gray-900 dark:text-white">Emendas Cloud</div>
            <div className="text-xs text-gray-500 dark:text-slate-400">Workspace / {breadcrumb.page}</div>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <span className="hidden rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 sm:inline-flex">
            Prefeitura de Sinop
          </span>
          <HelpMenu />
          <NotificationBell saas />
          <ThemeButton isDark={isDark} onClick={toggleDark} saas />
          <UserMenu name={user?.name ?? 'Usuario'} identityLabel={identityLabel} saas />
        </div>
      </header>
    );
  }

  return (
    <header className="dashboard-topbar-height sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 dark:border-slate-800 dark:bg-slate-950 sm:px-6">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <span className="hidden font-semibold text-slate-500 dark:text-slate-400 sm:inline">{breadcrumb.section}</span>
          <span className="hidden text-slate-300 dark:text-slate-600 sm:inline">/</span>
          <span className="truncate font-bold text-slate-900 dark:text-slate-100">{breadcrumb.page}</span>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2.5">
        <HelpMenu />
        <NotificationBell />
        <ThemeButton isDark={isDark} onClick={toggleDark} />
        <UserMenu
          name={user?.name ?? 'Usuario'}
          identityLabel={`Perfil: ${roleLabel}${secretariaLabel ? ` · Vínculo: ${secretariaLabel}` : ''}`}
        />
      </div>
    </header>
  );
};

export default DashboardTopBar;
